// src/features/EnumTranslations/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { InputField, TextareaField } from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// Zod schema for the new-form
const FormSchema = z.object({
  class_name: z.string().nullable().optional(),
  column_name: z.string().nullable().optional(),
  enum_code: z.coerce.number().optional().nullable(),
  pg_code: z.string().nullable().optional(),
  enum_key: z.string().nullable().optional(),
  fr_translation: z.string().nullable().optional(),
  en_translation: z.string().nullable().optional(),
});

export function EnumTranslationsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                EnumTranslations {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Class Name & Column Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="class_name"
                    label={t(`${table_name}.columns.class_name`, {
                      ns: "tables",
                      defaultValue: "Class Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="column_name"
                    label={t(`${table_name}.columns.column_name`, {
                      ns: "tables",
                      defaultValue: "Column Name",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2: Enum Code & PG Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="enum_code"
                    label={t(`${table_name}.columns.enum_code`, {
                      ns: "tables",
                      defaultValue: "Enum Code",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="pg_code"
                    label={t(`${table_name}.columns.pg_code`, {
                      ns: "tables",
                      defaultValue: "PG Code",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3: Enum Key */}
                <div className="grid grid-cols-1 gap-4">
                  <InputField
                    name="enum_key"
                    label={t(`${table_name}.columns.enum_key`, {
                      ns: "tables",
                      defaultValue: "Enum Key",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 4: French Translation (full width textarea) */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="fr_translation"
                    label={t(`${table_name}.columns.fr_translation`, {
                      ns: "tables",
                      defaultValue: "French Translation",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.french_translation")}
                  />
                </div>

                {/* Row 5: English Translation (full width textarea) */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="en_translation"
                    label={t(`${table_name}.columns.en_translation`, {
                      ns: "tables",
                      defaultValue: "English Translation",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.english_translation")}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
